import { Box, Button, Stack, styled, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export function FileUploader({
  accept,
  onChange,
}: {
  accept?: string;
  onChange: (file: File | null) => void;
}) {
  const inputRef = useRef(null);
  const pasteZoneRef = useRef<HTMLInputElement | null>(null);

  /* ---------- auto focus ---------- */
  useEffect(() => {
    pasteZoneRef.current?.focus();
  }, []);

  /* ---------- helpers ---------- */
  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    onChange(file);
  };

  /* ---------- events ---------- */
  const handleChange = (e: any) => {
    handleFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handlePaste = (e: any) => {
    const text = e.clipboardData.getData("text");

    // convert pasted text → File
    const file = new File([text], "pasted.txt", {
      type: "text/plain",
    });
    handleFile(file);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        hidden
        type="file"
        accept={accept}
        capture="environment" // ✅ mobile camera
        onChange={handleChange}
      />

      {/* Paste / Upload / Drop Zone */}
      <Box
        ref={pasteZoneRef}
        tabIndex={0}
        // role="button"
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          position: "relative",
          border: "2px dashed",
          borderColor: "grey.400",
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          // cursor: "pointer",
          outline: "none",
          "&:focus": {
            borderColor: "primary.main",
            bgcolor: "action.hover",
          },
        }}
      >
        {/* drop */}
        <Stack direction={"column"} spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="body2">Click / Drag / Ctrl + V</Typography>
          <Typography variant="caption" color="text.secondary">
            Drop to Upload
          </Typography>

          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
          >
            Upload files
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => handleFile(event.target.files?.[0])}
              multiple
            />
          </Button>
        </Stack>
      </Box>
    </>
  );
}
